/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import env from '../../../env';
import { useGet, SiteLink } from '../../../components';
import { Loading, ErrorPopup } from '../../../components';
import { useAuth } from '../../../auth';
import utils from '../../../utils';

const s = {
  dashboard: {
    maxWidth: 900,
    margin: '0 auto',
    padding: '2rem 1rem',
  },
  header: {
    marginBottom: '2rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid #dee2e6',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 600,
    marginBottom: '0.25rem',
    color: '#002e5b',
  },
  section: {
    marginBottom: '2.5rem',
  },
  sectionTitle: {
    fontSize: '1.15rem',
    fontWeight: 700,
    marginBottom: '1rem',
    color: '#002e5b',
    paddingBottom: '0.4rem',
    borderBottom: '2px solid #002e5b',
  },
  orderCard: {
    marginBottom: '1.25rem',
    padding: '1rem 1.25rem',
    border: '1px solid #dee2e6',
    borderRadius: 8,
    backgroundColor: '#fff',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  orderHeader: {
    marginBottom: '0.75rem',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: '0.4rem 1rem',
  },
  orderNo: { fontWeight: 600, color: '#002e5b' },
  orderMeta: { color: '#6c757d', fontSize: '0.9rem' },
  itemList: { listStyle: 'none', paddingLeft: 0, marginBottom: 0 },
  itemRow: {
    padding: '0.6rem 0',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    borderBottom: '1px solid #f0f0f0',
    flexWrap: 'wrap',
    '&:last-child': { borderBottom: 'none' },
  },
  thumb: {
    width: 80,
    height: 60,
    borderRadius: 4,
    objectFit: 'cover',
    flexShrink: 0,
    backgroundColor: '#e9ecef',
  },
  thumbPlaceholder: {
    width: 80,
    height: 60,
    borderRadius: 4,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.6rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#fff',
  },
  itemInfo: { flex: 1, minWidth: 0 },
  itemName: { fontWeight: 600, fontSize: '0.95rem', marginBottom: 2 },
  itemMeta: { fontSize: '0.82rem', color: '#6c757d' },
  badge: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 999,
    fontSize: '0.75rem',
    fontWeight: 600,
    backgroundColor: '#d4edda',
    color: '#155724',
    flexShrink: 0,
  },
  emptyState: {
    padding: '2rem',
    textAlign: 'center',
    color: '#6c757d',
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
  },
  emptyActions: {
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginTop: '1rem',
  },
};

export function MemberDashboard() {
  const auth = useAuth();
  const get = useGet();

  React.useEffect(() => {
    get.send((env.apiBase || '').replace(/\/$/, '') + '/api/order/MyOrders');
    // eslint-disable-next-line
  }, []);

  const displayName = auth.user?.displayName || auth.user?.email?.split('@')[0] || 'Member';

  return (
    <div css={s.dashboard}>
      <header css={s.header}>
        <h1 css={s.title}>My account</h1>
        <p className="text-muted" style={{ marginBottom: 0 }}>
          Welcome back, {displayName}. Here you can view your purchase history and access your content.
        </p>
      </header>

      {get.loading() && <Loading />}
      {get.hasErrors() && <ErrorPopup title="Error loading orders" errors={get.errors} />}

      {!get.loading() && !get.hasErrors() && (() => {
        const orders = Array.isArray(get.response) ? get.response : [];

        // Separate orders by type
        const liveStreamOrders = orders.filter(o =>
          (o.orderItems || o.OrderItems || [])
            .filter(i => Number(i.orderItemType ?? i.OrderItemType ?? 0) === 0)
            .some(i => i.watchToken || i.WatchToken || (i.ext?.liveFeed || i.Ext?.LiveFeed))
        );
        const dartfishOrders = orders.filter(o =>
          (o.orderItems || o.OrderItems || [])
            .filter(i => Number(i.orderItemType ?? i.OrderItemType ?? 0) === 0)
            .some(i => i.ext?.dartfish || i.Ext?.Dartfish || i.isDartfishOnly || o.isDartfishOnly)
        );
        // Any orders not matched to either (e.g. standard products)
        const otherOrders = orders.filter(o =>
          !liveStreamOrders.includes(o) && !dartfishOrders.includes(o)
        );

        const hasAnyOrders = orders.length > 0;

        return (
          <>
            <section css={s.section} aria-labelledby="livestream-heading">
              <h2 id="livestream-heading" css={s.sectionTitle}>Live Stream Purchases</h2>
              {liveStreamOrders.length === 0 ? (
                <div css={s.emptyState}>
                  <p style={{ marginBottom: 0 }}>No live stream purchases yet.</p>
                  <div css={s.emptyActions}>
                    <SiteLink to="/live-feeds" className="btn btn-outline-primary">Browse Live Streams</SiteLink>
                  </div>
                </div>
              ) : (
                liveStreamOrders.map(order => (
                  <OrderCard key={order.orderId || order.OrderId} order={order} type="livefeed" />
                ))
              )}
            </section>

            <section css={s.section} aria-labelledby="dartfish-heading">
              <h2 id="dartfish-heading" css={s.sectionTitle}>Adelaide Footy TV Subscriptions</h2>
              {dartfishOrders.length === 0 ? (
                <div css={s.emptyState}>
                  <p style={{ marginBottom: 0 }}>No Adelaide Footy TV subscriptions yet.</p>
                  <div css={s.emptyActions}>
                    <SiteLink to="/media/adelaide-footy-tv" className="btn btn-outline-primary">Browse Adelaide Footy TV</SiteLink>
                  </div>
                </div>
              ) : (
                dartfishOrders.map(order => (
                  <OrderCard key={order.orderId || order.OrderId} order={order} type="dartfish" />
                ))
              )}
            </section>

            {otherOrders.length > 0 && (
              <section css={s.section} aria-labelledby="other-heading">
                <h2 id="other-heading" css={s.sectionTitle}>Other Purchases</h2>
                {otherOrders.map(order => (
                  <OrderCard key={order.orderId || order.OrderId} order={order} type="other" />
                ))}
              </section>
            )}

            {!hasAnyOrders && (
              <div css={s.emptyState}>
                <p style={{ marginBottom: 0 }}>You have no orders yet.</p>
                <div css={s.emptyActions}>
                  <SiteLink to="/live-feeds" className="btn btn-outline-primary">Browse Live Streams</SiteLink>
                  <SiteLink to="/media/adelaide-footy-tv" className="btn btn-outline-primary">Browse Adelaide Footy TV</SiteLink>
                </div>
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
}

function OrderCard({ order, type }) {
  const skuItems = (order.orderItems || order.OrderItems || [])
    .filter(i => Number(i.orderItemType ?? i.OrderItemType ?? 0) === 0);

  return (
    <div css={s.orderCard}>
      <div css={s.orderHeader}>
        <span css={s.orderNo}>Order {order.orderNo || order.OrderNo}</span>
        <span css={s.orderMeta}>{utils.date.formatYYYYMMDD(order.orderDate || order.OrderDate)}</span>
        <span css={s.orderMeta}>AU {utils.money.formatMoney(order.totalAmount || order.TotalAmount)}</span>
      </div>
      <ul css={s.itemList}>
        {skuItems.map(item => (
          <ItemRow key={item.orderItemId || item.OrderItemId} item={item} order={order} type={type} />
        ))}
      </ul>
    </div>
  );
}

function ItemRow({ item, order, type }) {
  const watchToken = item.watchToken || item.WatchToken;
  const name = item.itemName || item.ItemName || 'Item';
  const thumbnailUrl = item.thumbnailUrl || item.ThumbnailUrl;
  const posterUrl = item.ext?.liveFeed?.posterUrl || item.Ext?.LiveFeed?.PosterUrl;
  const imageUrl = posterUrl || thumbnailUrl;
  const club = order.specialRequest || order.SpecialRequest;

  const placeholderBg = type === 'dartfish' ? '#002e5b' : '#1a3a5c';
  const placeholderLabel = type === 'dartfish' ? 'Dartfish' : 'Live';

  return (
    <li css={s.itemRow}>
      {imageUrl ? (
        <img src={imageUrl} alt={name} css={s.thumb} />
      ) : (
        <div css={[s.thumbPlaceholder, { backgroundColor: placeholderBg }]}>
          {placeholderLabel}
        </div>
      )}
      <div css={s.itemInfo}>
        <div css={s.itemName}>{name}</div>
        {type === 'dartfish' && club && (
          <div css={s.itemMeta}>Club / Organisation: {club}</div>
        )}
      </div>
      {watchToken && (
        <SiteLink
          to={`/watch?t=${encodeURIComponent(watchToken)}`}
          className="btn btn-primary btn-sm"
        >
          Watch
        </SiteLink>
      )}
      {type === 'dartfish' && (
        <span css={s.badge}>Registered</span>
      )}
    </li>
  );
}
